import React, { useState, useEffect, useRef } from "react";
import { useBackNavigation } from "../../navigation/BackNavigationContext";

/**
 * PageHeader
 * - Sticky per-view header row that contains:
 *   - Left-aligned back button (when applicable)
 *   - Title (string or node)
 *   - Optional search field (between title and actions)
 *   - Optional right-side actions slot
 *
 * Notes:
 * - This component is intentionally lightweight and uses Tailwind utility classes.
 * - It expects the global TopBar to be non-fixed so the PageHeader can stick to the viewport top.
 * - Search prop: { value, onChange, placeholder, onClear? }
 * - On mobile: Shows search icon that expands into searchbox
 * - On larger screens: Always shows searchbox at 50% width
 */

const PageHeader = ({ title, subtitle, breadcrumb, actions, search, className = "" }) => {
  const { canGoBack, goBack } = useBackNavigation();
  const hasSearch = search && search.value !== undefined;
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close search when value is cleared (only if it had a value before)
  const [hadSearchValue, setHadSearchValue] = useState(false);
  
  useEffect(() => {
    if (hasSearch && search.value) {
      setHadSearchValue(true);
    } else if (hasSearch && !search.value && hadSearchValue && isSearchExpanded) {
      // Only close if we had a value before and now it's empty
      setIsSearchExpanded(false);
      setHadSearchValue(false);
    }
  }, [hasSearch, search?.value, isSearchExpanded, hadSearchValue]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSearchExpanded &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target) &&
        !event.target.closest('[aria-label="Toggle search"]')
      ) {
        setIsSearchExpanded(false);
      }
    };

    if (isSearchExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isSearchExpanded]);

  return (
    <div
      className={`sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-200 relative ${className}`}
      data-testid="page-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative">
        <div className={`${hasSearch || subtitle || breadcrumb ? "py-2" : "h-14"} flex flex-row items-center gap-2 sm:gap-3 relative`}>
          {/* Title section - hidden when search is expanded on mobile */}
          <div className={`flex items-center space-x-3 flex-shrink-0 transition-opacity duration-300 ${
            hasSearch && isSearchExpanded ? "md:opacity-100 opacity-0 invisible md:visible" : "opacity-100 visible"
          }`}>
            {canGoBack && (
              <button
                onClick={goBack}
                aria-label="Back"
                className="p-2 rounded-md hover:bg-gray-100 focus:outline-none"
              >
                <svg
                  className="h-5 w-5 text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
            )}
            <div>
              <div className="text-lg font-semibold text-gray-900">
                {typeof title === "string" ? title : title}
              </div>
              {subtitle && (
                <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
              )}
              {breadcrumb && <div className="mt-0.5">{breadcrumb}</div>}
            </div>
          </div>

          {/* Mobile: Animated search input overlay - expands from right */}
          {hasSearch && (
            <div
              ref={searchContainerRef}
              className={`md:hidden absolute right-0 top-0 bottom-0 flex items-center transition-all duration-300 ease-in-out origin-right ${
                isSearchExpanded
                  ? "left-0 opacity-100 pointer-events-auto"
                  : "w-10 opacity-0 pointer-events-none"
              }`}
            >
              <div className="w-full relative h-full flex items-center">
                <input
                  ref={searchInputRef}
                  value={search.value}
                  onChange={search.onChange}
                  placeholder={search.placeholder || "Search..."}
                  className={`w-full pr-10 px-3 py-2 border rounded text-sm transition-opacity duration-300 ${
                    isSearchExpanded ? "opacity-100" : "opacity-0"
                  }`}
                />
                {search.value && isSearchExpanded && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={search.onClear || (() => search.onChange({ target: { value: "" } }))}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none p-1"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 8.586l4.95-4.95a1 1 0 111.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95A1 1 0 013.636 14.95L8.586 10 3.636 5.05A1 1 0 015.05 3.636L10 8.586z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Desktop: Always visible search box at 50% width */}
          {hasSearch && (
            <div className="hidden md:flex md:flex-1 md:justify-center">
              <div className="w-[50%] relative">
                <input
                  value={search.value}
                  onChange={search.onChange}
                  placeholder={search.placeholder || "Search..."}
                  className="w-full pr-10 px-3 py-2 border rounded text-sm"
                />
                {search.value && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={search.onClear || (() => search.onChange({ target: { value: "" } }))}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 8.586l4.95-4.95a1 1 0 111.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95A1 1 0 013.636 14.95L8.586 10 3.636 5.05A1 1 0 015.05 3.636L10 8.586z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Actions section with search button on mobile */}
          <div className="flex-shrink-0 ml-auto flex items-center gap-2">
            {hasSearch && (
              <button
                onClick={() => {
                  const willExpand = !isSearchExpanded;
                  setIsSearchExpanded(willExpand);
                  // Focus the input after expansion animation starts
                  if (willExpand) {
                    setTimeout(() => {
                      searchInputRef.current?.focus();
                    }, 100);
                  }
                }}
                className={`md:hidden p-2 rounded-full border border-gray-300 hover:bg-gray-100 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors relative ${
                  isSearchExpanded ? "opacity-0 pointer-events-none" : "opacity-100"
                }`}
                aria-label="Toggle search"
              >
                <svg
                  className="h-5 w-5 text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {search.value && search.value.trim() && (
                  <span className="absolute top-0 right-0 h-2.5 w-2.5 bg-red-500 rounded-full border-2 border-white"></span>
                )}
              </button>
            )}
            {actions ? (
              <div className="flex-shrink-0">
                {actions}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default PageHeader;
